import type { FC } from 'react';
import type { FAQProps } from './types';

import { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  generateAccessKey
} from 'shared/helpers/index';
import { useWebsiteEditor } from 'shared/hooks/index';
import { Wrapper } from '../Common';
import { FirstItem } from './FirstItem';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import useSales from 'shared/hooks/useSales';

const Container = styled.div`
  padding: 80px 0;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 60px 0;
    `}
`;

const TitleWrapper = styled.div`
  text-align: center;
`;

const Title = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $headlineFont } }) => $headlineFont};
  font-weight: 700;
  margin-bottom: 18px;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 32px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Subtitle = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin-bottom: 36px;
  line-height: 1.35em;
  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

export const V3: FC<FAQProps> = (props) => {
  const {
    title = defaultWebValue,
    content = defaultWebValue,
    benefits = [],
    textColor,
    meta,
    hidden,
    customBackgroundColor,
    oppositeBackgroundColor,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { isDashboard } = useSales();

  const [current, setCurrent] = useState<number | undefined>(0);

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  // Accessibility improvements
  const finalBackgroundColor = useMemo(
    () => customBackgroundColor || '#fff',
    [customBackgroundColor]
  );

  const finalTextColor = useMemo(
    () => filterBackgroundColor(finalBackgroundColor, textColor),
    [finalBackgroundColor, textColor]
  );

  const titleAccessKey = generateAccessKey('t', order);
  const subtitleAccessKey = generateAccessKey('s', order);
  const skipLinkId = `skip-to-faq-v3-${meta['accessorKey'] || 'faq'}`;

  const handleFAQError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `FAQ section error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleFAQToggle = (index: number) => {
    try {
      setCurrent(current === index ? undefined : index);
    } catch (error) {
      handleFAQError(error);
    }
  };

  return (
    <Container
      className={containerClassname}
      aria-label="Frequently Asked Questions section"
      role="region"
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: finalBackgroundColor
      }}>
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to FAQ content</a>
      </span>

      <Wrapper id={skipLinkId}>
        <div>
          <TitleWrapper>
            <Title
              id={title.id}
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(title.val, process.env['APP_TYPE'] === 'dashboard')
              }}
              style={{ color: finalTextColor }}
              isEmpty={isTitleEmpty}
              {...(isDashboard
                ? {
                    onClick: (e: React.MouseEvent<HTMLElement>) => {
                      try {
                        updateActiveElement(e);
                      } catch (error) {
                        handleFAQError(error);
                      }
                    },
                    onBlur: resetActiveElement,
                    'data-accessor-parent': meta['accessorKey'],
                    'data-accessorkey': 'title',
                    'data-placeholder': '+',
                    editable: true
                  }
                : {
                    'aria-label':
                      isTitleEmpty || !title.val?.trim() ? 'FAQ section title' : undefined,
                    accessKey: titleAccessKey,
                    title: `Press Alt+${titleAccessKey} to focus this FAQ section title`,
                    tabIndex: 0
                  })}
            />

            <Subtitle
              id={content.id}
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(content.val, editable)
              }}
              style={{ color: finalTextColor }}
              isEmpty={isContentEmpty}
              {...(isDashboard
                ? {
                    onClick: (e: React.MouseEvent<HTMLElement>) => {
                      try {
                        updateActiveElement(e);
                      } catch (error) {
                        handleFAQError(error);
                      }
                    },
                    onBlur: resetActiveElement,
                    'data-accessor-parent': meta['accessorKey'],
                    'data-accessorkey': 'content',
                    'data-placeholder': '+',
                    editable: true
                  }
                : {
                    role: 'textbox',
                    'aria-multiline': 'true',
                    'aria-label':
                      isContentEmpty || !content.val?.trim()
                        ? 'FAQ section description'
                        : undefined,
                    accessKey: subtitleAccessKey,
                    title: `Press Alt+${subtitleAccessKey} to focus this FAQ section description`,
                    tabIndex: 0
                  })}
            />
          </TitleWrapper>

          <div
            role="group"
            aria-label="FAQ questions and answers"
            aria-describedby={`${title.id} ${content.id}`}>
            {benefits.length > 0 ? (
              <div role="list" aria-label={`${benefits.length} frequently asked questions`}>
                {benefits.map((faq, index) => {
                  const isTitleEmpty = checkTitleBenefits(index);
                  const isContentEmpty = checkContentBenefits(index);

                  return (
                    <div key={index} role="listitem">
                      <FirstItem
                        {...faq}
                        order={order}
                        index={index}
                        meta={meta}
                        textColor={textColor}
                        first={index === 0}
                        last={index === benefits.length - 1}
                        isOpen={current === index}
                        onClick={() => handleFAQToggle(index)}
                        isTitleEmpty={isTitleEmpty}
                        isContentEmpty={isContentEmpty}
                        customBackgroundColor={customBackgroundColor}
                        oppositeBackgroundColor={oppositeBackgroundColor}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                role="status"
                aria-label="No FAQ items available"
                style={{
                  display: websiteAssembler ? 'block' : 'none',
                  padding: '20px',
                  textAlign: 'center',
                  color: finalTextColor
                }}>
                No FAQ items to display
              </div>
            )}
          </div>
        </div>
      </Wrapper>
    </Container>
  );
};

V3.displayName = 'FAQV3';
